import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { type MaybeRef, computed, unref } from 'vue'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

// Fila del snapshot inmutable de la suscripción (subscriptions/active).
// featureCode/value vía ParseToCamelAfter; feature_code por si llega crudo.
interface RawSnapshotRow {
	featureCode?: string
	feature_code?: string
	value?: { included?: boolean; limit?: number | null } | null
}

interface RawActiveResponse {
	subscription?: {
		id?: number
		plan?: { code?: string } | null
		featuresSnapshot?: RawSnapshotRow[]
		features_snapshot?: RawSnapshotRow[]
	} | null
}

const getActiveSubscription = async (
	companyId: number
): Promise<RawActiveResponse> => {
	const { data } = await axios.get<RawActiveResponse>(
		`/api/companies/${companyId}/subscriptions/active`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

/**
 * ¿El plan de la empresa incluye una feature booleana (p.ej. approval_flows)?
 * Lee el snapshot inmutable de la suscripción activa — la misma fuente que usa
 * el backend (FeatureGate), así el candado del front coincide con el 422 del
 * server. Sin companyId o sin suscripción NO bloquea (locked=false): el
 * backend sigue siendo la fuente de verdad y responde 422 si aplica.
 */
const usePlanFeature = (
	companyId: MaybeRef<number | string | undefined>,
	featureCode: string
) => {
	const resolvedCompany = computed(() => {
		const raw = unref(companyId)
		const parsed = typeof raw === 'string' ? Number(raw) : raw
		return parsed && !Number.isNaN(parsed) ? parsed : undefined
	})

	const { data, isLoading } = useQuery({
		queryKey: computed(() => [
			'planFeature',
			resolvedCompany.value,
			featureCode,
		]),
		queryFn: () => getActiveSubscription(resolvedCompany.value as number),
		enabled: computed(() => !!resolvedCompany.value),
		retry: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
	})

	const subscription = computed(() => data.value?.subscription ?? null)

	const snapshot = computed<RawSnapshotRow[]>(
		() =>
			subscription.value?.featuresSnapshot ??
			subscription.value?.features_snapshot ??
			[]
	)

	// Bloqueada solo cuando la suscripción existe y la feature NO está incluida
	// (ausente del snapshot = no incluida: mismo criterio que PlanFeatureGuard).
	// Features de tope (ai_reports, bookings_per_month, cost_centers, users)
	// vienen como {limit} en vez de {included}: bloqueadas solo con limit 0
	// (null/positivo = incluida, aunque sea limitada). Sin esto, cualquier
	// feature de tope quedaría bloqueada para todos los planes.
	const locked = computed(() => {
		if (!resolvedCompany.value || !subscription.value) return false

		const row = snapshot.value.find(
			(r) => (r.featureCode ?? r.feature_code) === featureCode
		)
		const value = row?.value

		if (value && 'limit' in value) return value.limit === 0

		return value?.included !== true
	})

	return {
		locked,
		isLoading,
		subscriptionId: computed(() => subscription.value?.id),
		currentPlanCode: computed(() => subscription.value?.plan?.code),
	}
}

export default usePlanFeature
